/**
 * Partida contable (asiento de diario): tipos de partida, origen, líneas de
 * cargo y abono, validación de partida doble y reversa exacta.
 *
 * Reglas de una partida válida:
 * - al menos dos líneas;
 * - cada línea tiene exactamente un lado (debe o haber) mayor que cero y
 *   ninguno negativo, en centavos enteros;
 * - la suma del debe es igual a la suma del haber.
 * La base de datos vuelve a exigir las tres al confirmar la transacción.
 */
import { keyedEnum } from '../catalogs/enum.js';
import { cents, sumBy } from '../money/money.js';
/** Tipos de partida automática. */
export const JOURNAL_ENTRY_KINDS = [
    'disbursement',
    'payment',
    'recovery',
    'payment_reversal',
    'interest_accrual',
    'non_accrual_reversal',
    'reclassification',
    'provision',
    'write_off',
    'opening_balance',
];
/** Catálogo con clave: `JOURNAL_ENTRY_KIND.payment`. */
export const JOURNAL_ENTRY_KIND = keyedEnum(JOURNAL_ENTRY_KINDS);
/** Nombre en español de cada tipo de partida. */
export const JOURNAL_ENTRY_KIND_LABELS = {
    disbursement: 'Desembolso',
    payment: 'Cobro',
    recovery: 'Recuperación de cartera castigada',
    payment_reversal: 'Reversa de cobro',
    interest_accrual: 'Devengo de intereses',
    non_accrual_reversal: 'Suspensión de devengo',
    reclassification: 'Reclasificación de cartera',
    provision: 'Ajuste de estimación',
    write_off: 'Castigo de cartera',
    opening_balance: 'Saldos de apertura',
};
/** Operaciones que originan partidas (para enlazar la partida con su documento). */
export const JOURNAL_SOURCE_TYPES = ['disbursement', 'payment', 'loan', 'job'];
/** Catálogo con clave: `JOURNAL_SOURCE_TYPE.payment`. */
export const JOURNAL_SOURCE_TYPE = keyedEnum(JOURNAL_SOURCE_TYPES);
/** Nombre en español de cada origen. */
export const JOURNAL_SOURCE_TYPE_LABELS = {
    disbursement: 'Desembolso',
    payment: 'Pago',
    loan: 'Préstamo',
    job: 'Proceso automático',
};
/** Error de una partida que no cumple la partida doble. */
export class UnbalancedEntryError extends Error {
    constructor(message) {
        super(message);
        this.name = 'UnbalancedEntryError';
    }
}
/** Mínimo de líneas de una partida (un cargo y un abono). */
export const MIN_JOURNAL_LINES = 2;
/** Línea de cargo (debe). */
export function debitLine(role, amount, loanId = null) {
    return { role, debit: cents(amount), credit: cents(0), loanId };
}
/** Línea de abono (haber). */
export function creditLine(role, amount, loanId = null) {
    return { role, debit: cents(0), credit: cents(amount), loanId };
}
/**
 * Línea con signo: un monto positivo es un cargo y uno negativo un abono
 * por el valor absoluto. Útil para los ajustes por diferencia.
 */
export function signedLine(role, signedAmount, loanId = null) {
    return signedAmount >= 0 ? debitLine(role, signedAmount, loanId) : creditLine(role, -signedAmount, loanId);
}
/** Quita las líneas en cero (un componente que no se cobró no deja rastro). */
export function withoutZeroLines(lines) {
    return lines.filter((line) => line.debit !== 0 || line.credit !== 0);
}
/** Totales del debe y del haber. */
export function journalTotals(lines) {
    return { debit: sumBy(lines, (line) => line.debit), credit: sumBy(lines, (line) => line.credit) };
}
/** Lanza `UnbalancedEntryError` si la partida no cumple la partida doble. */
export function assertBalanced(lines) {
    if (lines.length < MIN_JOURNAL_LINES)
        throw new UnbalancedEntryError('La partida necesita al menos un cargo y un abono');
    for (const line of lines) {
        if (!Number.isSafeInteger(line.debit) || !Number.isSafeInteger(line.credit)) {
            throw new UnbalancedEntryError('Los montos de la partida deben ser centavos enteros');
        }
        if (line.debit < 0 || line.credit < 0)
            throw new UnbalancedEntryError('Una línea no puede tener montos negativos');
        const sidesUsed = Number(line.debit > 0) + Number(line.credit > 0);
        if (sidesUsed !== 1)
            throw new UnbalancedEntryError('Cada línea lleva un cargo o un abono, no ambos ni ninguno');
    }
    const totals = journalTotals(lines);
    if (totals.debit !== totals.credit) {
        throw new UnbalancedEntryError(`La partida no cuadra: debe ${String(totals.debit)} ≠ haber ${String(totals.credit)} (centavos)`);
    }
}
/**
 * Quita líneas en cero, valida la partida doble y devuelve la partida lista.
 * Todos los constructores de partidas terminan aquí.
 */
export function finalizeEntry(kind, description, lines) {
    const compact = withoutZeroLines(lines);
    assertBalanced(compact);
    return { kind, description, lines: compact };
}
/**
 * Reversa exacta de una partida: cada cargo se vuelve abono y viceversa,
 * con las mismas cuentas, préstamos y montos. Sumada a la original, cada
 * cuenta queda en cero.
 */
export function reverseLines(lines) {
    return lines.map((line) => ({ role: line.role, debit: line.credit, credit: line.debit, loanId: line.loanId }));
}
/** Convierte una línea guardada al modelo del dominio (p. ej. para reversarla). */
export function bookedLineOf(row) {
    return { role: row.accountRole, debit: cents(row.debit), credit: cents(row.credit), loanId: row.loanId };
}
/** Saldo neto (debe − haber) por rol de un conjunto de líneas. */
export function netByRole(lines) {
    const result = {};
    for (const line of lines)
        result[line.role] = (result[line.role] ?? 0) + line.debit - line.credit;
    return result;
}
//# sourceMappingURL=journal.js.map